// Keep the screen on while music plays.
// Phones and tablets pause web audio once the screen locks, so a gym that plays
// from a phone or tablet can keep it awake instead.
//
// Two ways, used together where needed:
// 1. The Screen Wake Lock API (Android, desktop, iPhone/iPad in Safari).
// 2. On iPhone and iPad, a tiny silent muted video looping out of sight. iOS
//    doesn't auto-lock while a video plays, and this also works in the
//    home-screen app, where Apple's wake lock doesn't hold.
// The browser drops both whenever the page is hidden, so they're taken again
// when the page comes back.

let sentinel = null
let wanted = false
let video = null

const hasWakeLockApi = () =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator

// iPhone, iPod and iPad (iPadOS 13+ reports itself as a Mac with a touch screen)
export const isAppleMobile = () => {
  try {
    const ua = window.navigator.userAgent || ''
    if (/iPhone|iPad|iPod/i.test(ua)) return true
    return /Macintosh/i.test(ua) && (window.navigator.maxTouchPoints || 0) > 1
  } catch (e) {
    return false
  }
}

export const isSupported = () => hasWakeLockApi() || isAppleMobile()

function getVideo () {
  if (video) return video
  const v = document.createElement('video')
  v.setAttribute('playsinline', '')
  v.setAttribute('webkit-playsinline', '')
  v.setAttribute('muted', '')
  v.setAttribute('aria-hidden', 'true')
  v.muted = true
  v.loop = true
  v.preload = 'auto'
  v.src = '/audio/keep-awake.mp4'
  // On the page but out of sight (iOS won't play a video that isn't in the page)
  Object.assign(v.style, {
    position: 'fixed', left: '0', bottom: '0', width: '2px', height: '2px',
    opacity: '0.01', pointerEvents: 'none', zIndex: '-1'
  })
  document.body.appendChild(v)
  video = v
  return v
}

function startVideo () {
  if (!isAppleMobile()) return
  try {
    const v = getVideo()
    if (!v.paused) return
    const p = v.play()
    if (p && p.catch) p.catch(() => {})
  } catch (e) {}
}

function stopVideo () {
  if (video && !video.paused) {
    try { video.pause() } catch (e) {}
  }
}

async function acquire () {
  if (!wanted) return
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
  startVideo()
  if (sentinel || !hasWakeLockApi()) return
  try {
    sentinel = await navigator.wakeLock.request('screen')
    sentinel.addEventListener('release', () => { sentinel = null })
    if (!wanted) release()
  } catch (e) {
    // Low battery mode or a browser policy can refuse it; the video still helps on iOS
    sentinel = null
  }
}

function release () {
  stopVideo()
  const s = sentinel
  sentinel = null
  if (s) s.release().catch(() => {})
}

export function keepAwake (on) {
  wanted = !!on
  if (wanted) acquire()
  else release()
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') acquire()
  })
  // iOS sometimes wants a tap before a video may start; any tap retries it
  document.addEventListener('touchend', () => { if (wanted) startVideo() }, { passive: true })
}

// The setting is remembered on this device. Home-screen apps start with it on.
const KEY = 'keepScreenOn'

export function savedSetting () {
  try {
    const v = window.localStorage.getItem(KEY)
    if (v === 'on') return true
    if (v === 'off') return false
  } catch (e) {}
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  } catch (e) {
    return false
  }
}

export function saveSetting (on) {
  try { window.localStorage.setItem(KEY, on ? 'on' : 'off') } catch (e) {}
}
